import {StackActions, useNavigation} from '@react-navigation/native';
import React, {FC, useEffect, useMemo} from 'react';
import {View} from 'react-native';
import Animated, {FadeIn} from 'react-native-reanimated';

import {FadeInOut} from '@/components/AnimationWrappers';
import Card from '@/components/Card/Card';
import CardButton from '@/components/CardButton';
import CollapsingText from '@/components/CollapsingText';
import Icon from '@/components/Icon';
import ImageWithPreview from '@/components/ImageWithPreview';
import Link from '@/components/Link';
import Loader from '@/components/Loader/Loader';
import ProviderLink from '@/components/ProviderLink/ProviderLink';
import Share from '@/components/Share/Share';
import Space from '@/components/Space/Space';
import {AutoLinkText} from '@/components/Text/AutoLinkText';
import Text from '@/components/Text/Text';
import TrackPlaylistToggler from '@/components/TrackPlaylistToggler/TrackPlaylistToggler';
import {useFavToggle} from '@/hooks/useFavToggle';
import {useAppDispatch} from '@/hooks/useRedux';
import {useCollect} from '@/modules/Collect';
import {useExternalSigner} from '@/modules/ExternalWallet';
import {playNewQueue} from '@/store/player';
import {useThemedStyles} from '@/theme';
import {ITrack, PlayContextType} from '@/types/common';
import {ImageSize} from '@/types/media';
import {RootStackNavigationParams, Routes} from '@/types/routes';
import {openLinkSafe} from '@/utils/linking';
import {isDesktopBrowser, isWeb} from '@/utils/platform';
import {getTrackUrl} from '@/utils/share';
import {stripHtmlString} from '@/utils/string';
import {getTrackArtists} from '@/utils/tracks';

import {styles} from './Track.style';

interface IProps {
  track: ITrack;
  showLoader: boolean;
  linkEnabled: boolean;
  description?: string;
  referral?: string;
  autoPlay?: boolean;
  redirect?: string;
}

const TrackInfo: FC<IProps> = ({
  track,
  showLoader,
  description,
  referral,
  autoPlay = false,
  redirect,
  linkEnabled,
}) => {
  const navigation = useNavigation<RootStackNavigationParams>();
  const style = useThemedStyles(styles);
  const dispatch = useAppDispatch();
  const {address} = useExternalSigner();

  const {
    collect,
    icon: collectIcon,
    isLoading: isCollectLoading,
    textId: collectTextId,
  } = useCollect(track, referral);

  const {isFav, toggleFav} = useFavToggle(track.id);

  const formattedDescription = useMemo(
    () => (description ? stripHtmlString(description) : ''),
    [description],
  );

  const getArtistLink = (slugOrId: string) => ({
    to: {
      screen: Routes.MainNavigation,
      params: {
        screen: Routes.Artist,
        params: {slugOrId},
      },
    },
    action: StackActions.push(Routes.Artist, {slugOrId}),
  });

  const trackLink = {
    to: {
      screen: Routes.MainNavigation,
      params: {
        screen: Routes.Track,
        params: {slug: track.slug, referral: referral},
      },
    },
  };

  const onPlay = () => {
    if (track) {
      dispatch(
        playNewQueue({
          trackId: track.id,
          trackIds: [track.id],
          context: {
            source: 'TrackDetails',
            type: PlayContextType.trackDetails,
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (autoPlay) {
      onPlay();

      if (redirect && !isWeb) {
        openLinkSafe(redirect);
        return;
      }

      if (!isDesktopBrowser) {
        navigation.navigate(Routes.Player);
      }
    }
  }, [autoPlay, redirect]);

  const trackTitle = (
    <Text size="l" weight="semibold">
      {track.title}
    </Text>
  );
  return (
    <>
      <Space style={style.header}>
        <Space flex pt="xxs">
          {linkEnabled ? (
            <Link {...trackLink}>{trackTitle}</Link>
          ) : (
            <>{trackTitle}</>
          )}
          <Space mt="xs" />

          <Text numberOfLines={2}>
            {getTrackArtists(track).map((artist, index) => (
              <React.Fragment key={artist.id}>
                {index > 0 && (
                  <View>
                    <Text>{' & '}</Text>
                  </View>
                )}
                <Link {...getArtistLink(artist.slug)}>
                  {({hover}) => <Text underline={hover}>{artist.name}</Text>}
                </Link>
              </React.Fragment>
            ))}
          </Text>

          <Space pt="xs" flex />
          <ProviderLink
            platformId={track.platformId}
            url={track.websiteUrl}
            analyticsInfo={{
              screen: Routes.Track,
              id: track.id,
            }}
            size="xs"
            referral={referral}
          />
        </Space>
        <Space ml="s" />
        <Card style={style.coverArtCard}>
          {track.lossyArtworkUrl && (
            <ImageWithPreview
              resizeWidth={ImageSize.track}
              source={{uri: track.lossyArtworkUrl}}
              style={style.coverArt}
              hasBorderRadius
            />
          )}
        </Card>
      </Space>
      <Space mt="m" style={style.actions}>
        <FadeInOut
          enabled={isCollectLoading}
          style={style.collectButtonWrapper}>
          <CardButton
            style={[style.collectButton, style.action]}
            onPress={collect}
            text={{id: collectTextId, size: 'xs'}}
            icon={{
              provider: 'custom',
              name: collectIcon,
              size: 18,
            }}
          />
        </FadeInOut>

        <Share url={getTrackUrl(track, address)} title={track.title}>
          {({onPress, icon: shareIcon}) => (
            <CardButton
              style={[style.action, style.actionIcon]}
              onPress={onPress}
              icon={{
                ...shareIcon,
                size: 18,
              }}
            />
          )}
        </Share>
        <TrackPlaylistToggler track={track}>
          {({openModal}) => (
            <CardButton
              style={[style.action, style.actionIcon]}
              onPress={openModal}
              icon={{
                name: 'playlistAdd',
                provider: 'custom',
                size: 18,
              }}
            />
          )}
        </TrackPlaylistToggler>
        <CardButton
          style={[style.action, style.actionIcon]}
          onPress={toggleFav}
          icon={{
            name: 'heart',
            provider: 'custom',
            fill: isFav,
            size: 18,
          }}
        />
        <CardButton
          style={[style.action, style.actionIcon]}
          onPress={onPlay}
          icon={{
            name: 'play',
            provider: 'custom',
            size: 22,
          }}
        />
      </Space>
      {showLoader ? (
        <Space pv="xxl">
          <Loader size="s" />
        </Space>
      ) : (
        <Animated.View entering={FadeIn}>
          {!!formattedDescription && (
            <Space mt="m">
              <Text weight="semibold" id="track.about" size="m" />
              <Space mt="xs" />
              <CollapsingText>
                <AutoLinkText size="xs">{formattedDescription}</AutoLinkText>
              </CollapsingText>
            </Space>
          )}
          <Space mt="s" style={style.headerRow}>
            <Icon name="trending-up" provider="material" size={18} />
            <Space ml="xs" />
            <Text weight="semibold" id="track.activity" size="m" />
          </Space>
        </Animated.View>
      )}
    </>
  );
};

export default React.memo(TrackInfo);
